import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Button, TextField, MenuItem, Chip, Typography } from "@material-ui/core";

import select from "../../utils/select";
import KPTable from "../../components/KPTable/KPTable";
import { filterTopics, deleteTopic, submitTopic, exportStatistics } from "../../reducer/researchTopic/action";
import { notification } from "../../reducer/home/action";
import {
  TOPIC_LEVELS,
  TOPIC_STATUS,
  TOPIC_DEADLINE_BUCKETS,
  statusLabel,
  statusColor,
  levelLabel,
} from "../../constants/topicStatus";
import ResearchTopicFormModal from "./ResearchTopicFormModal";
import ResearchTopicDetailModal from "./ResearchTopicDetailModal";

const daysUntil = (finishAt) => {
  if (!finishAt) return null;
  const diff = (new Date(finishAt).getTime() - Date.now()) / (24 * 3600 * 1000);
  return Math.round(diff);
};

const relativeUpdate = (updatedAt) => {
  if (!updatedAt) return "";
  const days = Math.floor((Date.now() - new Date(updatedAt).getTime()) / (24 * 3600 * 1000));
  if (days <= 0) return "Hôm nay";
  return `${days} ngày trước`;
};

export default function ResearchTopicTable({ scope }) {
  const dispatch = useDispatch();
  const role = useSelector((s) => select(s, "userReducer", "role"));
  const myProfile = useSelector((s) => {
    const v = select(s, "profileReducer", "detail");
    return v && v.toJS ? v.toJS() : v;
  });
  const list = useSelector((s) => {
    const v = select(s, "researchTopicReducer", "list");
    return v && v.toJS ? v.toJS() : v || [];
  });
  const isFetching = useSelector((s) => select(s, "researchTopicReducer", "isFetching"));

  const isAdmin = role === "sudo-admin";
  // Giảng viên chỉ xem đề tài của mình; Admin xem toàn trường.
  const scopedProfileId = isAdmin ? 0 : myProfile?.id || 0;

  const [filters, setFilters] = useState({ year: "", level: "", status: "", deadlineBucket: "", keyword: "" });
  const [formOpen, setFormOpen] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const reloadPayload = useMemo(() => {
    const p = { profileId: scopedProfileId };
    if (filters.year) p.year = Number(filters.year);
    if (filters.level) p.level = filters.level;
    if (filters.status) p.status = filters.status;
    if (filters.deadlineBucket) p.deadlineBucket = filters.deadlineBucket;
    if (filters.keyword) p.keyword = filters.keyword;
    return p;
  }, [scopedProfileId, filters]);

  useEffect(() => {
    dispatch(filterTopics(reloadPayload));
  }, [dispatch, reloadPayload]);

  const openCreate = () => { setEditTopic(null); setFormOpen(true); };
  const openEdit = (row) => { setEditTopic(row); setFormOpen(true); };

  const columns = [
    {
      title: <b>Tác vụ</b>, key: "action", width: "14%",
      render: (_, row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <Typography variant="caption" color="primary" style={{ cursor: "pointer" }} onClick={() => setDetailId(row.id)}>Chi tiết</Typography>
          {/* Chỉ khi Nháp mới sửa/nộp/xoá được (backbone chặt) */}
          {row.status === "draft" && (
            <Typography variant="caption" style={{ cursor: "pointer" }} onClick={() => openEdit(row)}>Sửa</Typography>
          )}
          {row.status === "draft" && (
            <Typography variant="caption" color="primary" style={{ cursor: "pointer" }}
              onClick={() => {
                if (!row.proposalFileUrl) {
                  dispatch(notification({ type: "error", message: "Vui lòng nộp file thuyết minh trước khi nộp đề tài." }));
                  return;
                }
                dispatch(submitTopic(row.id, reloadPayload));
              }}>Nộp</Typography>
          )}
          {row.status === "draft" && (
            <Typography variant="caption" color="secondary" style={{ cursor: "pointer" }}
              onClick={() => window.confirm("Xoá đề tài này?") && dispatch(deleteTopic(row.id, reloadPayload))}>Xoá</Typography>
          )}
        </div>
      ),
    },
    { title: <b>Mã</b>, key: "code", width: "8%", sortable: true, searchable: true },
    { title: <b>Tên đề tài</b>, key: "titleVi", sortable: true, searchable: true },
    {
      title: <b>Chủ nhiệm</b>, key: "_leader", width: "12%",
      render: (_, row) => {
        const leader = (row.members || []).find((m) => m.role === "leader");
        return leader ? (leader.fullName || leader.profile?.name || "") : (row.createdByName || "");
      },
    },
    { title: <b>Cấp</b>, key: "_level", width: "10%", render: (_, row) => levelLabel(row.level) },
    {
      title: <b>Trạng thái</b>, key: "_status", width: "10%",
      render: (_, row) => <Chip size="small" label={statusLabel(row.status)} style={{ backgroundColor: statusColor(row.status), color: "#fff" }} />,
    },
    { title: <b>Người khai báo</b>, key: "createdByName", width: "10%" },
    {
      title: <b>Sắp hạn</b>, key: "_deadline", width: "9%",
      render: (_, row) => {
        const d = daysUntil(row.finishAt);
        if (d === null) return "";
        if (d < 0) return <span style={{ color: "#f44336" }}>Quá {Math.abs(d)} ngày</span>;
        return <span style={{ color: d <= 7 ? "#ff9800" : undefined }}>Còn {d} ngày</span>;
      },
    },
    { title: <b>Cập nhật cuối</b>, key: "_updated", width: "10%", render: (_, row) => relativeUpdate(row.updatedAt) },
  ];

  return (
    <div>
      {/* Bộ lọc */}
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 12 }}>
        <Grid item><Typography variant="h6">Quản lý đề tài {isAdmin ? "(toàn trường)" : "(của tôi)"}</Typography></Grid>
        <Grid item xs />
        <Grid item>
          <TextField size="small" label="Tìm kiếm" value={filters.keyword} onChange={(e) => setFilters({ ...filters, keyword: e.target.value })} />
        </Grid>
        <Grid item>
          <TextField size="small" label="Năm học" type="number" style={{ width: 100 }} value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })} />
        </Grid>
        <Grid item>
          <TextField size="small" select label="Cấp" style={{ width: 140 }} value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })}>
            <MenuItem value="">Tất cả</MenuItem>
            {TOPIC_LEVELS.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item>
          <TextField size="small" select label="Trạng thái" style={{ width: 150 }} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <MenuItem value="">Tất cả</MenuItem>
            {Object.keys(TOPIC_STATUS).map((k) => <MenuItem key={k} value={k}>{TOPIC_STATUS[k].label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item>
          <TextField size="small" select label="Sắp hạn" style={{ width: 150 }} value={filters.deadlineBucket} onChange={(e) => setFilters({ ...filters, deadlineBucket: e.target.value })}>
            {TOPIC_DEADLINE_BUCKETS.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={() => dispatch(exportStatistics(reloadPayload))}>Xuất Excel</Button>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={openCreate}>Thêm đề tài</Button>
        </Grid>
      </Grid>

      <KPTable columns={columns} rows={list} rowsPerPage={10} totalData={list.length} loading={isFetching} maxHeight="calc(100vh - 320px)" />

      <ResearchTopicFormModal
        open={formOpen}
        topic={editTopic}
        reloadPayload={reloadPayload}
        onClose={(saved) => { setFormOpen(false); if (saved) dispatch(filterTopics(reloadPayload)); }}
      />
      <ResearchTopicDetailModal
        open={!!detailId}
        topicId={detailId}
        reloadPayload={reloadPayload}
        onClose={() => { setDetailId(null); dispatch(filterTopics(reloadPayload)); }}
      />
    </div>
  );
}
